import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { MessageCircle, Send, User, PaperclipIcon, X, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { sendMessageToOpenAI } from '@/services/openaiService';
import { useToast } from '@/hooks/use-toast';

interface Message {
  sender: 'user' | 'ai';
  text: string;
  files?: File[];
}

const samplePrompts = ["How can you help me?", "I want to sell digital products.", "My business isn't even online yet."];

const AIAgentSection = () => {
  const [messages, setMessages] = useState<Message[]>([{
    sender: 'ai',
    text: "Hi there! I'm KnightAI, your strategic growth partner. Tell me about your business, and I'll help seize its best interest."
  }]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const openAIApiKey = import.meta.env.VITE_OPENAI_API_KEY || '';

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim() && uploadedFiles.length === 0) return;

    const messageText = message.trim() ? message : "Shared file(s)";

    setMessages(prev => [...prev, {
      sender: 'user',
      text: messageText,
      files: uploadedFiles.length > 0 ? [...uploadedFiles] : undefined
    }]);
    
    setInputMessage('');
    setUploadedFiles([]);

    setIsTyping(true);

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));
      
      conversationHistory.unshift({
        role: 'system',
        content: 'You are KnightAI, a strategic business consultant designed to help businesses grow. You provide actionable advice and focus on understanding user needs before suggesting solutions. Be friendly, professional and clear. You are an expert in business strategy, marketing, and digital transformation. Keep responses concise (under 150 words) and action-oriented.'
      });
      
      conversationHistory.push({
        role: 'user',
        content: messageText
      });

      const aiResponse = await sendMessageToOpenAI(conversationHistory, openAIApiKey);
      
      setIsTyping(false);
      setMessages(prev => [...prev, {
        sender: 'ai',
        text: aiResponse
      }]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      setIsTyping(false);
      
      toast({
        title: "Failed to get AI response",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      
      setMessages(prev => [...prev, {
        sender: 'ai',
        text: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment."
      }]);
    }
  };

  const handlePromptClick = (prompt: string) => {
    handleSendMessage(prompt);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsUploading(true);
      
      setTimeout(() => {
        const newFiles = Array.from(e.target.files || []);
        setUploadedFiles(prev => [...prev, ...newFiles]);
        setIsUploading(false);
      }, 1000);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const renderFilePreview = (file: File) => {
    const isImage = file.type.startsWith('image/');
    
    if (isImage) {
      return (
        <div className="relative w-24 h-24 bg-deepshadow/30 rounded-md overflow-hidden">
          <img 
            src={URL.createObjectURL(file)} 
            alt={file.name} 
            className="w-full h-full object-cover"
          />
        </div>
      );
    }
    
    return (
      <div className="relative flex items-center p-2 bg-deepshadow/30 rounded-md">
        <PaperclipIcon className="mr-2 h-4 w-4" />
        <span className="text-xs truncate max-w-[100px]">{file.name}</span>
      </div>
    );
  };

  return <section id="ai-agent" className="py-16 bg-knightblack text-softwhite">
      <div className="section-container">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="md:w-1/2">
            <h2 className="text-softwhite text-3xl md:text-4xl font-bold mb-6 text-neutral-50">
              Let <span className="text-goldcore">KnightAI</span> Guide You In Real-Time
            </h2>
            <p className="text-softwhite/80 mb-8 text-lg">Our AI agent will help you identify the perfect fit for your business. Chat now to start the process.</p>
            
            <div className="space-y-3 mb-8">
              <p className="text-goldcore font-medium">Try asking:</p>
              {samplePrompts.map((prompt, index) => <button key={index} onClick={() => handlePromptClick(prompt)} className="block w-full text-left p-3 bg-deepshadow/30 hover:bg-deepshadow/50 rounded-lg transition-colors">
                  "{prompt}"
                </button>)}
            </div>
            
            <div className="bg-deepshadow/20 p-6 rounded-lg border border-goldcore/20">
              <p className="font-medium mb-3">Prefer to talk to a human?</p>
              <Button variant="dark" size="default" asChild>
                <Link to="/chat">Book a Live Expert</Link>
              </Button>
            </div>
          </div>
          
          <div className="md:w-1/2 bg-deepshadow/30 rounded-xl overflow-hidden border border-goldcore/20 shadow-xl">
            <div className="bg-knightblack p-4 flex items-center border-b border-goldcore/20">
              <div className="h-10 w-10 rounded-full bg-goldcore/20 flex items-center justify-center mr-3">
                <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="KnightAI" className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold">KnightAI</p>
                <p className="text-xs text-softwhite/60">Online • Ready when you are</p>
              </div>
            </div>
            
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {messages.map((message, index) => (
                <div key={index} className={`flex ${message.sender === 'user' ? 'justify-end' : ''}`}>
                  {message.sender === 'ai' && (
                    <div className="h-8 w-8 rounded-full bg-goldcore/20 flex-shrink-0 flex items-center justify-center mr-2">
                      <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="KnightAI" className="h-5 w-5" />
                    </div>
                  )}
                  
                  <div className={`max-w-[80%] p-3 rounded-lg ${message.sender === 'user' ? 'bg-tacticalblue/30 rounded-tr-none ml-2' : 'bg-deepshadow/70 rounded-tl-none'}`}>
                    <p>{message.text}</p>
                    
                    {message.files && message.files.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.files.map((file: File, fileIndex: number) => (
                          <div key={fileIndex} className="relative">
                            {renderFilePreview(file)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {message.sender === 'user' && (
                    <div className="h-8 w-8 rounded-full bg-tacticalblue/30 flex-shrink-0 flex items-center justify-center ml-2">
                      <User size={16} className="text-softwhite" />
                    </div>
                  )}
                </div>
              ))}
              
              {isTyping && <div className="flex">
                  <div className="h-8 w-8 rounded-full bg-goldcore/20 flex-shrink-0 flex items-center justify-center mr-2">
                    <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="KnightAI" className="h-5 w-5" />
                  </div>
                  <div className="bg-deepshadow/70 rounded-lg rounded-tl-none p-4">
                    <div className="flex space-x-1">
                      <div className="h-2 w-2 rounded-full bg-softwhite/70 animate-bounce"></div>
                      <div className="h-2 w-2 rounded-full bg-softwhite/70 animate-bounce" style={{
                    animationDelay: '0.2s'
                  }}></div>
                      <div className="h-2 w-2 rounded-full bg-softwhite/70 animate-bounce" style={{
                    animationDelay: '0.4s'
                  }}></div>
                    </div>
                  </div>
                </div>}
            </div>
            
            {uploadedFiles.length > 0 && (
              <div className="px-4 py-2 border-t border-goldcore/20 flex flex-wrap gap-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    {renderFilePreview(file)}
                    <button 
                      onClick={() => removeFile(index)}
                      className="absolute -top-2 -right-2 bg-knightblack rounded-full p-1 opacity-90 hover:opacity-100"
                    >
                      <X size={14} className="text-victorycoral" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="p-4 border-t border-goldcore/20">
              <div className="relative">
                <input 
                  type="text" 
                  value={inputMessage} 
                  onChange={e => setInputMessage(e.target.value)} 
                  onKeyDown={e => e.key === 'Enter' && handleSendMessage()} 
                  placeholder="Type your message..." 
                  className="w-full bg-deepshadow/50 border border-goldcore/20 rounded-full py-3 px-4 pr-24 text-softwhite focus:outline-none focus:ring-2 focus:ring-goldcore/50" 
                />
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  multiple
                />
                
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  <button 
                    onClick={triggerFileInput}
                    disabled={isUploading}
                    className="h-8 w-8 rounded-full bg-deepshadow/70 text-goldcore flex items-center justify-center hover:bg-deepshadow"
                  >
                    {isUploading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <PaperclipIcon size={16} />
                    )}
                  </button>
                  
                  <button 
                    onClick={() => handleSendMessage()} 
                    className="h-8 w-8 rounded-full bg-goldcore text-knightblack flex items-center justify-center"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};

export default AIAgentSection;
